import React, { useState } from 'react';
import styles from './AddScrimTracks.module.css';
import { createScrimTracks, getYoutubeInfo } from '../utils/api';

interface AddScrimTracksProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingYoutubeIds: string[];
  scrimId: string;
  existingStreamStartTimes?: Date[]; // For 6-hour window validation
}

const AddScrimTracks: React.FC<AddScrimTracksProps> = ({
  isOpen,
  onClose,
  onSuccess,
  existingYoutubeIds,
  scrimId,
  existingStreamStartTimes = []
}) => {
  const [urls, setUrls] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  // Random example YouTube URLs for placeholder
  const exampleUrls = [
    'https://www.youtube.com/watch?v=xK4J57Nq8wY',
    'https://www.youtube.com/live/pL9V22Kx7Hw',
    'https://youtu.be/mR5X24tP2Zk'
  ];

  const extractYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];
    
    for (const pattern of patterns) {
      const match = url.trim().match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const validateUrls = (urlText: string): string[] => {
    if (!urlText.trim()) return [];
    
    const lines = urlText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    return lines;
  };

  const handleAdd = async () => {
    setError('');
    setIsAdding(true);
    
    try {
      const urlLines = validateUrls(urls);
      if (urlLines.length === 0) {
        setError('Please enter at least one YouTube URL');
        return;
      }

      setProgress('Validating YouTube URLs...');

      // Extract and validate YouTube IDs (silently deduplicate)
      const invalidUrls: string[] = [];
      const seenIds = new Set<string>();
      const uniqueVideos: { youtubeId: string; originalUrl: string; lineNumber: number }[] = [];

      for (let i = 0; i < urlLines.length; i++) {
        const url = urlLines[i];
        const youtubeId = extractYouTubeId(url);
        
        if (!youtubeId) {
          invalidUrls.push(`Line ${i + 1}: "${url}" - Invalid YouTube URL format`);
          continue;
        }

        // Check against existing youtubeIDs
        if (existingYoutubeIds.includes(youtubeId)) {
          invalidUrls.push(`Line ${i + 1}: "${url}" - This video is already in the scrim`);
          continue;
        }

        // Silently skip duplicates within the new URLs
        if (seenIds.has(youtubeId)) {
          continue;
        }
        seenIds.add(youtubeId);
        uniqueVideos.push({ youtubeId, originalUrl: url, lineNumber: i + 1 });
      }

      // Enforce maximum total tracks (existing + new)
      const totalTracks = existingYoutubeIds.length + uniqueVideos.length;
      if (totalTracks > 6) {
        setError(`Maximum 6 tracks allowed. You have ${existingYoutubeIds.length} existing tracks. You can add up to ${6 - existingYoutubeIds.length} more.`);
        return;
      }

      if (invalidUrls.length > 0) {
        setError(`Invalid URLs found:\n${invalidUrls.join('\n')}`);
        return;
      }

      // Validate each YouTube ID with our API and collect video info
      setProgress('Checking video availability...');
      const validationErrors: string[] = [];
      const videoInfos: any[] = [];

      for (let i = 0; i < uniqueVideos.length; i++) {
        const { youtubeId, originalUrl, lineNumber } = uniqueVideos[i];
        try {
          const videoInfo = await getYoutubeInfo(youtubeId);
          videoInfos.push({ ...videoInfo, lineNumber, originalUrl });
        } catch (err) {
          validationErrors.push(`Line ${lineNumber}: "${originalUrl}" - Video not found or unavailable`);
        }
      }

      if (validationErrors.length > 0) {
        setError(`Video validation failed:\n${validationErrors.join('\n')}`);
        return;
      }

      // Check that all videos are streams with start times
      setProgress('Validating stream times...');
      const streamStartTimes: Date[] = [];
      const videoTimeErrors: string[] = [];

      for (const videoInfo of videoInfos) {
        if (!videoInfo.stream_start_utc) {
          videoTimeErrors.push(`Line ${videoInfo.lineNumber}: "${videoInfo.originalUrl}" - This video is not a streamed video. Only streamed videos can be synced as scrim tracks.`);
          continue;
        }

        try {
          const streamStart = new Date(videoInfo.stream_start_utc);
          if (isNaN(streamStart.getTime())) {
            videoTimeErrors.push(`Line ${videoInfo.lineNumber}: "${videoInfo.originalUrl}" - Invalid stream start time`);
            continue;
          }
          streamStartTimes.push(streamStart);
        } catch (err) {
          videoTimeErrors.push(`Line ${videoInfo.lineNumber}: "${videoInfo.originalUrl}" - Invalid stream start time format`);
        }
      }

      if (videoTimeErrors.length > 0) {
        setError(`Stream time validation failed:\n${videoTimeErrors.join('\n')}`);
        return;
      }

      // Check if all streams (existing + new) started within 6 hours of each other
      const allStreamStartTimes = [...existingStreamStartTimes, ...streamStartTimes];
      if (allStreamStartTimes.length > 1) {
        const sortedTimes = [...allStreamStartTimes].sort((a, b) => a.getTime() - b.getTime());
        const earliestTime = sortedTimes[0];
        const latestTime = sortedTimes[sortedTimes.length - 1];
        const timeDifferenceHours = (latestTime.getTime() - earliestTime.getTime()) / (1000 * 60 * 60);

        if (timeDifferenceHours > 6) {
          const formatTime = (date: Date) => date.toISOString().replace('T', ' ').replace('Z', ' UTC');
          setError(
            `Youtube video stream time validation failed:\n` +
            `Youtube streams must have started within 6 hours of each other.\n` +
            `Earliest stream: ${formatTime(earliestTime)}\n` +
            `Latest stream: ${formatTime(latestTime)}\n` +
            `Time difference: ${timeDifferenceHours.toFixed(1)} hours`
          );
          return;
        }
      }

      // Create all tracks in a single API call
      setProgress('Adding tracks...');
      const startTrackIndex = existingYoutubeIds.length + 1;
      const tracksToCreate = uniqueVideos.map(({ youtubeId }, index) => ({
        youtube_id: youtubeId,
        track_index: startTrackIndex + index
      }));
      
      await createScrimTracks(scrimId, tracksToCreate);

      // Reset form and close modal
      setUrls('');
      setError('');
      onSuccess();

    } catch (err) {
      console.error('Error adding tracks:', err);
      setError(err instanceof Error ? err.message : 'Failed to add tracks');
    } finally {
      setIsAdding(false);
      setProgress('');
    }
  };

  const handleClose = () => {
    if (!isAdding) {
      setUrls('');
      setError('');
      setProgress('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3>Add Tracks</h3>
        <p>
          Paste YouTube URLs for the videos you want to add to this scrim.
          Each URL should be on a separate line.
        </p>
        <p className={styles.inputDescription}>
          Maximum {6 - existingYoutubeIds.length} more track(s) allowed (6 total).
        </p>
        
        <div className={styles.inputGroup}>
          <label htmlFor="urls">YouTube URLs</label>
          <textarea
            id="urls"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            placeholder={exampleUrls.join('\n')}
            rows={6}
            className={styles.urlsTextarea}
            disabled={isAdding}
          />
        </div>

        {error && (
          <div className={styles.error}>
            <i className="bi bi-exclamation-triangle"></i>
            <pre>{error}</pre>
          </div>
        )}

        {isAdding && (
          <div className={styles.progress}>
            <div className={styles.spinner}></div>
            <span>{progress}</span>
          </div>
        )}

        <div className={styles.modalActions}>
          <button
            className={styles.cancelButton}
            onClick={handleClose}
            disabled={isAdding}
          >
            Cancel
          </button>
          <button
            className={styles.addButton}
            onClick={handleAdd}
            disabled={isAdding}
          >
            {isAdding ? 'Adding...' : 'Add Tracks'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddScrimTracks;


